import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Trash2, ChevronRight, MessageCircle } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase, type StyleRequest } from "@/lib/supabase";
import { AppShell } from "@/components/app-shell";
import { EmptyState } from "@/components/empty-state";
import { HintBanner } from "@/components/hint-banner";
import { Button } from "@/components/ui/button";
import {
  CollectionToolbar,
  applyTextSearch,
  type ActiveFilters,
  type FilterFieldDef,
  type SortOption,
} from "@/components/collection-toolbar";
import { MOOD_PRESETS } from "@/components/smart-select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EVENT_PRESETS = ["Dinner", "Work", "Church", "Casual", "Travel", "Celebration"];
const SEASON_PRESETS = ["Spring", "Summer", "Fall", "Winter", "All Seasons"];
const TIME_PRESETS = ["Morning", "Afternoon", "Evening"];

export const Route = createFileRoute("/requests/")({
  head: () => ({
    meta: [
      { title: "Style Requests — Cailève" },
      { name: "description", content: "Your saved styling requests." },
    ],
  }),
  component: () => (
    <AppShell>
      <RequestsList />
    </AppShell>
  ),
});

function RequestsList() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const qc = useQueryClient();
  const [pending, setPending] = useState<StyleRequest | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<ActiveFilters>({});
  const [sortValue, setSortValue] = useState<string>("recent");

  const { data, isLoading } = useQuery({
    queryKey: ["style_requests", uid],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("style_requests")
        .select("*")
        .eq("user_id", uid)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message || "Network error");
      return (data ?? []) as StyleRequest[];
    },
  });

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: "event", label: "Event", type: "select", options: EVENT_PRESETS },
      { key: "season", label: "Season", type: "select", options: SEASON_PRESETS },
      { key: "time_of_day", label: "Time of Day", type: "select", options: TIME_PRESETS },
      { key: "mood", label: "Mood", type: "select", options: [...MOOD_PRESETS] },
    ],
    [],
  );

  const sortOptions: SortOption[] = [
    { value: "recent", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "event_asc", label: "Event A → Z" },
  ];

  const displayItems = useMemo(() => {
    let items = data ?? [];
    if (typeof filters.event === "string" && filters.event)
      items = items.filter((r) => r.event === filters.event);
    if (typeof filters.season === "string" && filters.season)
      items = items.filter((r) => r.season === filters.season);
    if (typeof filters.time_of_day === "string" && filters.time_of_day)
      items = items.filter((r) => r.time_of_day === filters.time_of_day);
    if (typeof filters.mood === "string" && filters.mood) {
      const m = (filters.mood as string).toLowerCase();
      items = items.filter((r) =>
        (r.mood ?? "").toLowerCase().split(",").map((s) => s.trim()).includes(m),
      );
    }
    items = applyTextSearch(items, searchQuery, ["request_name", "event"]);
    const sorted = [...items];
    switch (sortValue) {
      case "oldest": sorted.sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? "")); break;
      case "event_asc": sorted.sort((a, b) => (a.event ?? "").localeCompare(b.event ?? "")); break;
      default: sorted.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    }
    return sorted;
  }, [data, filters, searchQuery, sortValue]);

  const hasActiveFilters =
    !!searchQuery ||
    Object.values(filters).some((v) => v !== undefined && v !== "" && v !== false);

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("style_requests").delete().eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Style removed.");
      setPending(null);
      qc.invalidateQueries({ queryKey: ["style_requests", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const bulkDel = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("style_requests").delete().in("id", ids);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success(`${selectedIds.size} ${selectedIds.size === 1 ? "request" : "requests"} removed.`);
      setSelectedIds(new Set());
      setEditMode(false);
      qc.invalidateQueries({ queryKey: ["style_requests", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-8 py-12">
      <div className="flex items-end justify-between border-b border-border pb-6 mb-10">
        <div>
          <h1 className="font-serif-display text-4xl">Style Requests</h1>
        </div>
        <div className="flex items-center gap-3">
          {!isLoading && (data?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => { setEditMode((v) => !v); setSelectedIds(new Set()); }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "Inter, sans-serif",
                fontSize: 11,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: editMode ? "var(--caileve-lilac-text)" : "var(--caileve-muted)",
                padding: 0,
              }}
            >
              {editMode ? "Done" : "Edit"}
            </button>
          )}
          <Link to="/suggest">
            <Button className="bg-[var(--caileve-gold)] text-[var(--caileve-bg)] hover:opacity-90" style={{ borderRadius: "var(--caileve-radius-pill)" }}>
              New Request
            </Button>
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="divide-y divide-border border-t border-b border-border">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between gap-4 py-5 pr-12">
              <div className="flex-1 space-y-2">
                <div className="animate-pulse h-7 w-48 rounded-sm" style={{ backgroundColor: "var(--caileve-soft)" }} />
                <div className="animate-pulse h-2.5 w-28 rounded-sm" style={{ backgroundColor: "var(--caileve-soft)" }} />
              </div>
              <div className="animate-pulse h-4 w-4 rounded-sm" style={{ backgroundColor: "var(--caileve-soft)" }} />
            </div>
          ))}
        </div>
      ) : !data?.length ? (
        <EmptyState
          icon={MessageCircle}
          message="Tell Cailève where you are going and how you want to feel. Your personal stylist will do the rest."
        />
      ) : (
        <>
          <HintBanner
            hintKey="hint_request_first"
            show={(data?.length ?? 0) >= 1}
            message="Tell Cailève how you want to feel and it will style you from your own collection."
          />
          <CollectionToolbar
            searchPlaceholder="Search your requests…"
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filterFields={filterFields}
            activeFilters={filters}
            onFilterChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
            onClearFilters={() => setFilters({})}
            sortOptions={sortOptions}
            sortValue={sortValue}
            onSortChange={setSortValue}
          />
          {hasActiveFilters && (
            <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] mb-6">
              {displayItems.length} {displayItems.length === 1 ? "request" : "requests"}
            </p>
          )}
          {displayItems.length === 0 ? (
            <div className="border border-dashed border-[var(--caileve-soft)] py-24 text-center">
              <p
                className="font-serif-display text-2xl"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-lilac)" }}
              >
                No requests match your search.
              </p>
              <button
                type="button"
                onClick={() => { setSearchQuery(""); setFilters({}); }}
                className="mt-6 px-6 py-2 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)]"
                style={{ backgroundColor: "var(--caileve-lilac)" }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
        <ul className="divide-y divide-border border-t border-b border-border">
          {displayItems.map((r) => (
            <li key={r.id} className="relative group">
              {editMode ? (
                <button
                  type="button"
                  onClick={() => toggleSelect(r.id)}
                  className="relative z-10 flex items-center justify-between gap-4 py-5 pr-12 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  style={{ background: "none", border: "none", width: "100%", textAlign: "left" }}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: selectedIds.has(r.id) ? "var(--caileve-lilac)" : "rgba(255,255,255,0.85)",
                        border: selectedIds.has(r.id) ? "none" : "1.5px solid var(--caileve-muted)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "background-color 0.15s",
                        flexShrink: 0,
                      }}
                    >
                      {selectedIds.has(r.id) && (
                        <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                          <path d="M4 10l4 4 8-8" stroke="var(--caileve-bg)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif-display text-2xl leading-tight truncate">
                        {r.request_name || "Untitled Request"}
                      </p>
                      {(() => {
                        const meta = [r.event, r.season, r.time_of_day]
                          .filter((v): v is string => !!v && v.trim().length > 0)
                          .join(" · ");
                        return meta ? (
                          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                            {meta}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </button>
              ) : (
                <a
                  href={`/requests/${encodeURIComponent(r.id)}`}
                  className="relative z-10 flex items-center justify-between gap-4 py-5 pr-12 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-serif-display text-2xl leading-tight truncate">
                      {r.request_name || "Untitled Request"}
                    </p>
                    {(() => {
                      const meta = [r.event, r.season, r.time_of_day]
                        .filter((v): v is string => !!v && v.trim().length > 0)
                        .join(" · ");
                      return meta ? (
                        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                          {meta}
                        </p>
                      ) : null;
                    })()}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </a>
              )}
              {!editMode && (
                <button
                  type="button"
                  aria-label="Delete request"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPending(r);
                  }}
                  className="absolute right-0 top-1/2 z-20 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-full border border-border bg-background text-[var(--caileve-copper)] hover:text-[var(--caileve-error)] hover:border-[var(--caileve-error)]/50 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              )}
            </li>
          ))}
        </ul>
          )}
        </>
      )}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this request?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pending?.request_name || "Untitled Request"}" will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (pending) del.mutate(pending.id);
              }}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {editMode && selectedIds.size > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "calc(env(safe-area-inset-bottom) + 80px)",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            padding: "0 24px",
            zIndex: 50,
          }}
        >
          <button
            type="button"
            onClick={() => bulkDel.mutate(Array.from(selectedIds))}
            disabled={bulkDel.isPending}
            style={{
              backgroundColor: "var(--caileve-lilac)",
              color: "var(--caileve-bg)",
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              border: "none",
              borderRadius: 4,
              padding: "14px 32px",
              cursor: bulkDel.isPending ? "default" : "pointer",
              opacity: bulkDel.isPending ? 0.6 : 1,
              width: "100%",
              maxWidth: 400,
            }}
          >
            {bulkDel.isPending ? "Removing…" : `Remove ${selectedIds.size} ${selectedIds.size === 1 ? "request" : "requests"}`}
          </button>
        </div>
      )}
    </div>
  );
}
